import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UserOutlined } from '@ant-design/icons';
import { Avatar, Layout } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/overlayscrollbars.css';
import * as commonAPI from '@apis/common';
import * as paymentAPI from '@apis/paymentStatements';
import { ReactComponent as ChevronDownIcon } from '@assets/images/chevron-down-menu.svg';
import { ReactComponent as CloseIcon } from '@assets/images/x.svg';
import { Roles } from '@consts/role';
import { useAuthentication } from '@hooks/authentication';
import { useViewport } from '@hooks/viewport';
import { useAppUserSessionStore } from '@stores/appUserSessionStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { cdnImageUrl } from '@utils/image';

interface SiderProps {
  hide?: boolean;
  onHide?: () => void;
}

interface MenuSubItem {
  label: string;
  link: string;
  badgeCount?: number;
}

interface MenuItem {
  label: string;
  link?: string;
  badgeCount?: number;
  items?: MenuSubItem[];
}

interface MenuButtonProps {
  className?: string;
  label: string;
  badgeCount?: number;
  selected?: boolean;
  items?: MenuSubItem[];
  link?: string;
  onClick?: (link?: string) => void;
}

const MenuButton: React.FC<MenuButtonProps> = (props) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(() => {
    // localStorage에서 확장된 메뉴 목록을 불러와서 현재 메뉴가 포함되어 있는지 확인
    try {
      const expandedMenus = JSON.parse(localStorage.getItem('expandedMenus') || '[]');
      return expandedMenus.includes(props.label);
    } catch {
      return false;
    }
  });
  const currentPath = window.location.pathname;

  const hasChildren = props.items && props.items.length > 0;
  const isSelected = props.selected || props.items?.some((item) => item.link === currentPath);

  // 현재 경로가 변경될 때마다 해당하는 메뉴를 자동으로 펼치기 (사용자가 수동으로 닫지 않은 경우에만)
  useEffect(() => {
    if (hasChildren && props.items) {
      const shouldBeExpanded = props.items.some((item) => item.link === currentPath);
      if (shouldBeExpanded && !isExpanded) {
        // localStorage에서 사용자가 수동으로 닫았는지 확인
        try {
          const manuallyClosedMenus = JSON.parse(localStorage.getItem('manuallyClosedMenus') || '[]');
          if (!manuallyClosedMenus.includes(props.label)) {
            setIsExpanded(true);
            // localStorage에 자동으로 펼쳐진 메뉴 저장
            const expandedMenus = JSON.parse(localStorage.getItem('expandedMenus') || '[]');
            if (!expandedMenus.includes(props.label)) {
              expandedMenus.push(props.label);
              localStorage.setItem('expandedMenus', JSON.stringify(expandedMenus));
            }
          }
        } catch (error) {
          console.error('Failed to check manually closed menu state:', error);
        }
      }
    }
  }, [currentPath, hasChildren, props.items, isExpanded, props.label]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasChildren) {
      const newExpanded = !isExpanded;
      setIsExpanded(newExpanded);

      // localStorage에 확장된 메뉴 목록 저장
      try {
        const expandedMenus = JSON.parse(localStorage.getItem('expandedMenus') || '[]');
        if (newExpanded) {
          if (!expandedMenus.includes(props.label)) {
            expandedMenus.push(props.label);
          }
          // 사용자가 수동으로 펼쳤으므로 manuallyClosedMenus에서 제거
          const manuallyClosedMenus = JSON.parse(localStorage.getItem('manuallyClosedMenus') || '[]');
          const index = manuallyClosedMenus.indexOf(props.label);
          if (index > -1) {
            manuallyClosedMenus.splice(index, 1);
            localStorage.setItem('manuallyClosedMenus', JSON.stringify(manuallyClosedMenus));
          }
        } else {
          const index = expandedMenus.indexOf(props.label);
          if (index > -1) {
            expandedMenus.splice(index, 1);
          }
          // 사용자가 수동으로 닫았으므로 manuallyClosedMenus에 추가
          const manuallyClosedMenus = JSON.parse(localStorage.getItem('manuallyClosedMenus') || '[]');
          if (!manuallyClosedMenus.includes(props.label)) {
            manuallyClosedMenus.push(props.label);
            localStorage.setItem('manuallyClosedMenus', JSON.stringify(manuallyClosedMenus));
          }
        }
        localStorage.setItem('expandedMenus', JSON.stringify(expandedMenus));
      } catch (error) {
        console.error('Failed to save expanded menu state:', error);
      }
    } else if (props.link) {
      props.onClick?.(props.link);
    } else if (props.label === '로그아웃') {
      props?.onClick?.();
    }
  };

  const handleSubmenuClick = (e: React.MouseEvent, link: string) => {
    e.stopPropagation();
    props.onClick?.(link);
  };

  return (
    <div>
      <button
        className={`link-button ${props.className}`}
        style={{
          display: 'flex',
          height: '52px',
          alignItems: 'center',
          justifyContent: 'space-between',
          color: '#FFF',
          fontFamily: 'Pretendard',
          fontSize: '16px',
          fontStyle: 'normal',
          fontWeight: isSelected ? 600 : 500,
          lineHeight: '24px',
          letterSpacing: '-0.16px',
          width: '100%',
          borderRadius: '8px',
          padding: '0 20px 0 40px',
          backgroundColor: isSelected || isHovered ? 'var(--White-10, rgba(255, 255, 255, 0.10))' : 'transparent',
          transition: 'all 0.2s ease-in-out',
          border: 'none',
          cursor: 'pointer',
          margin: '4px 0',
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {props.label}
          {props?.badgeCount !== undefined && props?.badgeCount > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '14px',
                padding: '0 4px',
                height: '14px',
                marginLeft: '6px',
                borderRadius: '20px',
                backgroundColor: '#FF3D8F',
                color: '#FFF',
                fontSize: '11px',
                fontWeight: 600,
                lineHeight: '14px',
              }}>
              {props.badgeCount}
            </div>
          )}
        </div>
        {hasChildren && (
          <ChevronDownIcon
            style={{
              width: '16px',
              height: '16px',
              transition: 'transform 0.2s ease-in-out',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              opacity: isSelected ? 1 : 0.7,
            }}
          />
        )}
      </button>
      {hasChildren && isExpanded && (
        <div>
          {props.items?.map((item, index) => (
            <button
              key={`submenu-${index}`}
              style={{
                display: 'flex',
                height: '44px',
                alignItems: 'center',
                justifyContent: 'space-between',
                color:
                  item.link === currentPath ? 'var(--White-100, #FFF)' : 'var(--White-70, rgba(255, 255, 255, 0.70))',
                fontFamily: 'Pretendard',
                fontSize: '15px',
                fontStyle: 'normal',
                fontWeight: item.link === currentPath ? 600 : 500,
                lineHeight: '22px',
                letterSpacing: '-0.15px',
                width: '100%',
                padding: '0 20px 0 40px',
                borderRadius: '6px',
                backgroundColor:
                  item.link === currentPath ? 'var(--White-10, rgba(255, 255, 255, 0.10))' : 'transparent',
                transition: 'all 0.2s ease-in-out',
                border: 'none',
                cursor: 'pointer',
              }}
              onClick={(e) => item.link && handleSubmenuClick(e, item.link)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {item.label}
                {item.badgeCount !== undefined && item.badgeCount > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '14px',
                      padding: '0 4px',
                      height: '14px',
                      marginLeft: '6px',
                      borderRadius: '20px',
                      backgroundColor: '#FF3D8F',
                      color: '#FFF',
                      fontSize: '11px',
                      fontWeight: 600,
                      lineHeight: '14px',
                    }}>
                    {item.badgeCount}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Sider: React.FC<SiderProps> = (props) => {
  const { isMobile } = useViewport();
  const { authStore, logout } = useAuthentication({ disableLoginRefresh: true });
  const dialog = useOkCancelDialog();
  const navigate = useNavigate();
  const sessionStore = useAppUserSessionStore();
  const platformPaymentLastDate = localStorage.getItem('platform-payment-statements-last-date') || '0';
  const platformCheckLastDate = localStorage.getItem('platform-payment-statements-check-date') || '0';
  const naverPaymentLastDate = localStorage.getItem('naver-payment-statements-last-date') || '0';
  const naverCheckLastDate = localStorage.getItem('naver-payment-statements-check-date') || '0';

  // Restore scroll position after component mounts
  useEffect(() => {
    const restoreScroll = () => {
      const viewport = document.querySelector('.sider-scroll .os-viewport') as HTMLElement;
      if (viewport) {
        const savedScrollTop = sessionStorage.getItem('sider-scroll-position');
        if (savedScrollTop) {
          viewport.scrollTop = parseInt(savedScrollTop, 10);
        }
      }
    };

    // Use setTimeout to ensure DOM is ready
    const timer = setTimeout(restoreScroll, 100);
    return () => clearTimeout(timer);
  }, []);

  // Save scroll position before navigation
  const saveScrollPosition = () => {
    const viewport = document.querySelector('.sider-scroll .os-viewport') as HTMLElement;
    if (viewport) {
      sessionStorage.setItem('sider-scroll-position', viewport.scrollTop.toString());
    }
  };

  const newUpdatedCountsQuery = useQuery({
    queryKey: [`new-updated-counts-${authStore?.user?.id}`, sessionStore.lastCheckDates],
    queryFn: () => commonAPI.getNewUpdatedCounts(sessionStore.lastCheckDates),
  });
  const updatedPaymentStatementsCount = newUpdatedCountsQuery?.data?.data?.paymentStatements || 0;
  const updatedSalesChannelOrdersCount = newUpdatedCountsQuery?.data?.data?.saleChannelOrders || 0;

  const { data } = useQuery({
    queryKey: [`payment-statements-lastDate-${authStore.user?.id}`],
    queryFn: paymentAPI.getPaymentStatementLastDate,
    staleTime: 1 * 60 * 1000,
    enabled: false,
  });

  useEffect(() => {
    if (data?.data) {
      const lastDateData = data.data;
      if (lastDateData.platform) {
        localStorage.setItem('platform-payment-statements-last-date', lastDateData.platform.toString());
      }
      if (lastDateData.naver) {
        localStorage.setItem('naver-payment-statements-last-date', lastDateData.naver.toString());
      }
    }
  }, [data?.data]);

  const isNewBadgeMenu = (link: string | undefined) => {
    if (!link) return false;

    if (link === '/platform-orders' && Number(platformPaymentLastDate) > Number(platformCheckLastDate)) {
      return true;
    }
    if (link === '/channel-orders' && Number(naverPaymentLastDate) > Number(naverCheckLastDate)) {
      return true;
    }
    if (link === '/product-orders' && Number(naverPaymentLastDate) > Number(naverCheckLastDate)) {
      return true;
    }
    return false;
  };

  if (props.hide || window.location.pathname === '/notFound') {
    return <div id="hidden-sider"></div>;
  }

  const renderMenuButton = (item: MenuItem, index: number = 0) => {
    const currentPath = window.location.pathname;
    return (
      <MenuButton
        key={`menu-button-${index}`}
        className={isNewBadgeMenu(item.link) ? 'new-badge' : ''}
        label={item.label}
        link={item.link}
        items={item.items}
        badgeCount={item.badgeCount}
        selected={item.link === currentPath}
        onClick={(link) => {
          if (link) {
            saveScrollPosition();
            if (isMobile) {
              props.onHide?.();
            }
            navigate(link);
          }
        }}
      />
    );
  };

  return (
    <Layout.Sider
      width={280}
      style={{
        backgroundColor: '#242424',
        position: isMobile ? 'fixed' : 'static',
        zIndex: 997,
        top: !isMobile ? '81px' : '0px',
        height: !isMobile ? 'calc(100dvh - 81px)' : '100dvh',
      }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '20px',
          gap: '12px',
          borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
          position: 'relative',
          padding: '20px 0',
        }}>
        {isMobile && (
          <div
            style={{ position: 'absolute', top: '-4px', right: '16px' }}
            onClick={() => {
              props.onHide && props.onHide();
            }}>
            <CloseIcon />
          </div>
        )}
        <div>
          {authStore.user?.profile?.imageUrl ? (
            <img
              style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }}
              src={cdnImageUrl(authStore.user?.profile?.imageUrl)}
              alt=""
            />
          ) : (
            <Avatar size={60} icon={<UserOutlined />} />
          )}
        </div>
        {authStore.user?.roles?.includes(Roles.INSTRUCTOR) && (
          <div
            style={{
              color: '#FFF',
              fontFamily: 'Pretendard',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 700,
            }}>
            {authStore.user?.partners?.map((partner) => partner.nickname).join(', ')}
          </div>
        )}
        <div
          style={{ color: '#FFF', fontFamily: 'Pretendard', fontSize: '18px', fontStyle: 'normal', fontWeight: 700 }}>
          {authStore.user?.profile?.nickname || authStore.user?.profile?.name}
        </div>
      </div>
      <OverlayScrollbarsComponent
        className="sider-scroll"
        defer
        options={{
          scrollbars: { autoHide: 'scroll' },
          overflow: {
            x: 'hidden',
          },
        }}
        style={{
          marginTop: 0,
          position: 'relative',
          maxHeight: !isMobile ? 'calc(100dvh - 81px - 156px)' : 'calc(100dvh - 156px)',
          overflow: 'auto',
          width: '100%',
        }}>
        <div
          style={{
            borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
          }}>
          {renderMenuButton({ label: '대시보드', link: '/dashboard' })}
        </div>
        <div
          style={{
            borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
          }}>
          {renderMenuButton({
            label: '내 정보',
            items: [
              { label: '파트너 프로필', link: '/profile' },
              { label: '계정 정보 관리', link: '/account' },
            ],
          })}
        </div>
        {(authStore.user?.roles?.includes(Roles.PARTNER) ||
          authStore.user?.roles?.includes(Roles.SELLER) ||
          authStore.user?.roles?.includes(Roles.TUTOR)) && (
          <div
            style={{
              borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
            }}>
            {renderMenuButton({
              label: '상품 관리',
              items: [
                { label: '상품 목록', link: '/products' },
                { label: '라스트찬스', link: '/last-chance-products' },
                ...(authStore.user?.id === 'daSWMA4Rgm65bjnsx'
                  ? [{ label: '예약 성사 관리', link: '/reservation-success' }]
                  : []),
              ],
            })}
          </div>
        )}
        {(authStore.user?.roles?.includes(Roles.PARTNER) ||
          authStore.user?.roles?.includes(Roles.SELLER) ||
          authStore.user?.roles?.includes(Roles.TUTOR)) && (
          <div
            style={{
              borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
            }}>
            {renderMenuButton({
              label: '판매 관리',
              link: '/product-orders',
              badgeCount: updatedPaymentStatementsCount + updatedSalesChannelOrdersCount,
            })}
          </div>
        )}
        {(authStore.user?.roles?.includes(Roles.PARTNER) ||
          authStore.user?.roles?.includes(Roles.SELLER) ||
          authStore.user?.roles?.includes(Roles.TUTOR)) && (
          <div
            style={{
              borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
            }}>
            {renderMenuButton({ label: '정산 관리', link: '/settlements' })}
          </div>
        )}
        {authStore.user?.roles?.includes(Roles.INSTRUCTOR) && (
          <div
            style={{
              borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
            }}>
            {renderMenuButton({
              label: '수업 관리',
              items: [
                { label: '나의 일정', link: '/my-schedule' },
                { label: '리포트 관리', link: '/reports' },
              ],
            })}
          </div>
        )}
        {(authStore.user?.roles?.includes(Roles.PARTNER) ||
          authStore.user?.roles?.includes(Roles.SELLER) ||
          authStore.user?.roles?.includes(Roles.TUTOR)) &&
          authStore.user?.id === 'daSWMA4Rgm65bjnsx' && (
            <div
              style={{
                borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
              }}>
              {renderMenuButton({
                label: '수업 관리',
                items: [
                  { label: '나의 일정', link: '/my-schedule' },
                  { label: '그룹 관리', link: '/program-schedules' },
                  { label: '그룹 배정 설정', link: '/grouping-policy-settings' },
                  { label: '강사 관리', link: '/instructors' },
                  { label: '리포트 관리', link: '/reports' },
                ].filter((item) => {
                  if (item.link === '/program-schedules' || item.link === '/instructors') {
                    return (
                      authStore.user?.roles?.includes(Roles.PARTNER) ||
                      authStore.user?.roles?.includes(Roles.SELLER) ||
                      authStore.user?.roles?.includes(Roles.TUTOR)
                    );
                  }
                  return true;
                }),
              })}
            </div>
          )}
        {/* {(authStore.user?.roles?.includes(Roles.PARTNER) ||
          authStore.user?.roles?.includes(Roles.SELLER) ||
          authStore.user?.roles?.includes(Roles.TUTOR)) && (
          <div
            style={{
              borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
            }}>
            {renderMenuButton({
              label: '채팅',
              items: [
                { label: '채팅 목록', link: '/chat' },
                { label: '채팅 관리', link: '/chat-setting' },
              ],
            })}
          </div>
        )} */}
        {(authStore.user?.roles?.includes(Roles.PARTNER) ||
          authStore.user?.roles?.includes(Roles.SELLER) ||
          authStore.user?.roles?.includes(Roles.TUTOR) ||
          authStore.user?.roles?.includes(Roles.EDITOR) ||
          authStore.user?.roles?.includes(Roles.ADMIN)) && (
          <div
            style={{
              borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
            }}>
            {renderMenuButton({
              label: '콘텐츠 관리',
              items: [
                { label: '라운지 콘텐츠', link: '/user-contents' },
                { label: '후기 관리', link: '/reviews' },
              ],
            })}
          </div>
        )}
        {authStore.user?.roles?.includes(Roles.TUTOR) && (
          <div
            style={{
              borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
            }}>
            {renderMenuButton({
              label: '수강증 관리',
              items: [
                // { label: '수강증 이력', link: '/program-invoices' },
                { label: '수강증 이력', link: '/invoices' },
              ],
            })}
          </div>
        )}
        {authStore.user?.roles?.includes(Roles.MEMBERSHIP_PARTNER) && (
          <div
            style={{
              borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
            }}>
            {renderMenuButton({
              label: '멤버십 관리',
              items: [
                { label: '멤버십 회원 관리', link: '/membership-users' },
                { label: '멤버십 상품 조회', link: '/membership-products' },
              ],
            })}
          </div>
        )}
        <div
          style={{
            borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
          }}>
          {renderMenuButton({ label: '공지사항', link: '/notice' })}
        </div>
        <div
          style={{
            borderBottom: '1px solid var(--White-10, rgba(255, 255, 255, 0.10))',
          }}>
          <MenuButton
            label="로그아웃"
            onClick={() => {
              dialog.open({
                title: '로그아웃',
                content: '로그아웃 할까요?',
                type: 'ok_cancel',
                onConfirm: () => {
                  props.onHide && props.onHide();
                  logout();
                },
              });
            }}
          />
        </div>
      </OverlayScrollbarsComponent>
    </Layout.Sider>
  );
};

export default Sider;
